import React from 'react';
import { Trash2Icon, UserIcon, PhoneIcon, MailIcon } from 'lucide-react';
export type PersonData = {
  id: string;
  fullName: string;
  mobile: string;
  email: string;
  relationship: string;
  connection: string;
  notes: string;
};
type PersonEntryProps = {
  person: PersonData;
  index: number;
  onChange: (id: string, field: keyof PersonData, value: string) => void;
  onRemove: (id: string) => void;
};
const RELATIONSHIPS = ["Bride's Parent", "Groom's Parent", "Bride's Sibling", "Groom's Sibling", 'Nani / Nana side (Maternal Grandparents)', 'Dadi / Dada side (Paternal Grandparents)', 'Mama (Maternal Uncle)', 'Mami (Maternal Aunt)', 'Chacha (Paternal Uncle)', 'Chachi (Paternal Aunt)', 'Taya (Older Paternal Uncle)', 'Tayee (Older Paternal Aunt)', 'Massi (Maternal Aunt)', 'Massar (Maternal Uncle-in-law)', 'Bua (Paternal Aunt)', 'Fufa (Paternal Uncle-in-law)', 'Cousin', 'Aunt', 'Uncle', 'Friend', 'Family Friend', 'Bridesmaid', 'Groomsman', 'In-law', 'Relative', 'Other'];
const CONNECTIONS = ['Bride side', 'Groom side', 'Both families', 'Friend of couple', "Parent's guest", 'Extended family', 'Not sure'];
export function PersonEntry({
  person,
  index,
  onChange,
  onRemove
}: PersonEntryProps) {
  return <div className="bg-white rounded-2xl p-5 md:p-6 border border-amber-100 shadow-sm relative group transition-all hover:shadow-md" data-id="element-595">
      <div className="flex justify-between items-center mb-4" data-id="element-596">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2" data-id="element-597">
          <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold" data-id="element-598">
            {index + 1}
          </div>
          Guest Details
        </h4>
        {index > 0 && <button onClick={() => onRemove(person.id)} className="text-gray-400 hover:text-rose-500 transition-colors p-2 rounded-full hover:bg-rose-50" aria-label="Remove person" data-id="element-599">
            <Trash2Icon className="h-4 w-4" data-id="element-600" />
          </button>}
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-id="element-601">
        {/* Full Name */}
        <div className="md:col-span-2" data-id="element-602">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-603">
            Full Name *
          </label>
          <div className="relative" data-id="element-604">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-605">
              <UserIcon className="h-4 w-4 text-gray-400" data-id="element-606" />
            </div>
            <input type="text" value={person.fullName} onChange={e => onChange(person.id, 'fullName', e.target.value)} className="pl-10 w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all" placeholder="e.g. Rahul Sharma" required data-id="element-607" />
          </div>
        </div>

        {/* Mobile */}
        <div data-id="element-608">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-609">
            WhatsApp / Mobile *
          </label>
          <div className="relative" data-id="element-610">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-611">
              <PhoneIcon className="h-4 w-4 text-gray-400" data-id="element-612" />
            </div>
            <input type="tel" value={person.mobile} onChange={e => onChange(person.id, 'mobile', e.target.value)} className="pl-10 w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all" placeholder="+1 (555) 000-0000" required data-id="element-613" />
          </div>
        </div>

        {/* Email */}
        <div data-id="element-614">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-615">
            Email <span className="text-gray-400 font-normal" data-id="element-616">(Optional)</span>
          </label>
          <div className="relative" data-id="element-617">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-618">
              <MailIcon className="h-4 w-4 text-gray-400" data-id="element-619" />
            </div>
            <input type="email" value={person.email} onChange={e => onChange(person.id, 'email', e.target.value)} className="pl-10 w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all" placeholder="rahul@example.com" data-id="element-620" />
          </div>
        </div>

        {/* Relationship */}
        <div data-id="element-621">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-622">
            Relationship *
          </label>
          <select value={person.relationship} onChange={e => onChange(person.id, 'relationship', e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all bg-white" required data-id="element-623">
            <option value="" data-id="element-624">Select relationship...</option>
            {RELATIONSHIPS.map(rel => <option key={rel} value={rel} data-id="element-625">
                {rel}
              </option>)}
          </select>
        </div>

        {/* Connection */}
        <div data-id="element-626">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-627">
            Connection *
          </label>
          <select value={person.connection} onChange={e => onChange(person.id, 'connection', e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all bg-white" required data-id="element-628">
            <option value="" data-id="element-629">How are they connected?</option>
            {CONNECTIONS.map(conn => <option key={conn} value={conn} data-id="element-630">
                {conn}
              </option>)}
          </select>
        </div>

        {/* Notes */}
        <div className="md:col-span-2" data-id="element-631">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-632">
            Notes <span className="text-gray-400 font-normal" data-id="element-633">(Optional)</span>
          </label>
          <input type="text" value={person.notes} onChange={e => onChange(person.id, 'notes', e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all" placeholder="e.g. Needs help downloading the app" data-id="element-634" />
        </div>
      </div>
    </div>;
}